<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-blue" sprite="lighthouse" />
          Accounts
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-blue" sprite="lighthouse" />
            Accounts
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input
            class="mr-10"
            v-model="search"
            placeholder="Search"
            clearable
          />
          <el-dropdown v-if="isUserPlatformAdmin">
            <el-button>
              Add New
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="accountType = 'basic'">
                <v-svg class="voxo-blue" sprite="lighthouse" />
                Basic Account
              </el-dropdown-item>
              <el-dropdown-item @click.native="accountType = 'partner'">
                <v-svg class="voxo-aqua" sprite="world-hand" />
                Partner Account
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button @click="showModal('Create')" v-else-if="userCanCreate">
            Add New
          </el-button>
          <el-checkbox v-model="isDisabled">Disabled</el-checkbox>
        </div>

        <div class="box right">
          <el-pagination
            v-if="total > limit"
            layout="prev, pager, next"
            small
            background
            :current-page="page"
            :page-size="150"
            :pager-count="5"
            :total="total"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
          />
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <!-- Account Form -->
    <account-form
      ref="accountForm"
      :visible="modalVisible"
      :context="modalContext"
      :recordId="modalModelId"
      :integrationOptions="integrationOptions"
      :timezoneOptions="timezoneOptions"
      :routingProfileOptions="routingProfileOptions"
      :partnerOptions="partnerOptions"
      :defaultPricing="defaultPricing"
      :conduitAccountOptions="conduitAccountOptions"
      :customProductOptions="customProductOptions"
      :accountType="accountType"
      :partner-products="partnerProducts"
      @modalClosed="handleModalClose"
      @refreshPartners="refreshPartnerList"
    />
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import { mapActions } from "vuex";
import accountForm from "./components/form";
import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";

export default {
  name: "TenantsTable",
  components: { accountForm },
  data() {
    return {
      accountType: "",
      search: "",
      isDisabled: false,
      visibleActionsId: "",
      modalModelId: "",
      modalVisible: false,
      modalContext: "",
      total: 0,
      page: 1,
      limit: 150,
      pages: null,
      integrationOptions: [],
      timezoneOptions: [],
      routingProfileOptions: [],
      partnerOptions: [],
      tableLoading: false,
      defaultPricing: [],
      customProductOptions: [],
      tableData: [],
      allTenants: [],
      partnerProducts: [],
      outboundCidNumbersOptions: [],
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="lighthouse" />
                </div>,
              ];
            },
          },
        },
        {
          field: "disabled",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <el-tooltip
                  content="Account disabled"
                  placement="top"
                  v-show={row.disabled === "on"}
                >
                  <i class="el-icon-error voxo-dark-red" />
                </el-tooltip>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          minWidth: 150,
          sortable: true,
          showOverflow: true,
        },
        {
          field: "tenantCode",
          title: "Code",
          minWidth: 75,
          sortable: true,
          showOverflow: false,
        },
        {
          field: "partnerId",
          title: "Partner",
          minWidth: 200,
          showOverflow: true,
          sortable: true,
          slots: {
            default: ({ row }) => {
              if (!row.partnerId) {
                return [<span class="text-muted text-center">—</span>];
              }
              const partner = this.partnerOptions.find(p => p.id === row.partnerId);
              return [<span>{partner ? partner.name : row.partnerId}</span>];
            }
          }
        },
        {
          field: "timezoneId",
          title: "Time Zone",
          minWidth: 150,
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              if (!row.timezoneId) {
                return [<span class="text-muted text-center">—</span>];
              }
              const tz = this.timezoneOptions.find(t => t.id === row.timezoneId);
              return [<span>{tz ? tz.description : row.timezoneId}</span>];
            },
          }
        },
        {
          field: "dateCreation",
          title: "Created Date",
          minWidth: 120,
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              if (!row.dateCreation) {
                return [<span class="text-muted text-center">—</span>];
              }
              const date = new Date(row.dateCreation);
              return [
                <span title={date.toLocaleString()}>
                  {date.toLocaleDateString()}
                </span>,
              ];
            },
          },
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.showModal("Edit", row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteRecord(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
      conduitAccountOptions: [],
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },

    isUserPlatformAdmin() {
      return this.user.userRole === 1;
    },

    userCanCreate() {
      const createRoles = [11];
      const userRole = this.$store.state.auth.user.userRole;
      return createRoles.includes(+userRole);
    },
  },

  watch: {
    page: debounce(function () {
      this.queryAccounts();
    }, 500),

    accountType(val) {
      if (val) {
        this.showModal("Create");
      }
    },

    user() {
      this.queryAccounts();
    },

    search: debounce(function () {
      this.queryAccounts();
    }, 500),

    isDisabled: debounce(function () {
      this.queryAccounts();
    }, 500),

    $route: {
      immediate: true,
      handler(to) {
        if (to.params.tenantId) {
          this.modalContext = "Edit";
          this.modalModelId = to.params.tenantId;
          this.modalVisible = true;
        }
      },
    },
  },

  methods: {
    ...mapActions("tenants", {
      removeAccount: "remove",
    }),

    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    handleModalClose() {
      this.modalVisible = false;
      this.modalModelId = "";
      this.accountType = "";
      this.modalContext = "";
    },

    queryAccounts() {
      this.tableLoading = true;
      const token = this.$store.state.auth.accessToken;

      let url = this.isDisabled
        ? `${apiV2Host}/admin/tenants/disabled?page=${this.page}&recordsPerPage=${this.limit}`
        : `${apiV2Host}/admin/tenants?page=${this.page}&recordsPerPage=${this.limit}`;

      if (this.search) {
        url = `${url}&query=${this.search}`;
      }

      axios
        .get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((res) => {
          this.tableLoading = false;
          const { records, totalRecords } = res.data;

          this.pages = Math.ceil(totalRecords / this.limit);
          this.total = totalRecords;
          this.page = this.page > this.pages ? 1 : this.page;
          this.tableData = [...records];
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    showModal(context, editId = null) {
      this.modalVisible = true;
      this.modalContext = context;
      this.modalModelId = editId;
    },

    //deleting operation
    deleteRecord(id) {
      this.$confirm(
        "Permanently delete this account? No Going Back!",
        "Warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(() => {
          this.removeAccount(id)
            .then((res) => {
              this.$notify({
                title: "Success",
                message: "Account " + res.name + " has been removed!",
                type: "success",
              });
              this.editVisible = false;
            })
            .catch((error) => {
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error",
              });
            });
        })
        .catch(() => true);
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.showModal("Edit", data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    getIntegrationOptions() {
      feathersClient
        .service("integration-options")
        .find({
          query: {
            active: 1,
          },
        })
        .then((res) => {
          this.integrationOptions = [...res];
        });
    },
    queryTimezones() {
      feathersClient
        .service("timezones")
        .find({
          query: {
            $sort: { name: 1 },
          },
        })
        .then((res) => (this.timezoneOptions = [...res]));
    },

    queryRoutingProfiles() {
      if (+this.user.userRole === 1) {
        feathersClient
          .service("routing-profiles")
          .find({
            query: {
              $sort: { name: 1 },
            },
          })
          .then((res) => {
            this.routingProfileOptions = [...res];
          });
      }
    },

    findPartnerAccounts() {
      feathersClient
        .service("partners")
        .find()
        .then((res) => {
          this.partnerOptions = res;
        });
    },

    getDefaultPricing() {
      feathersClient
        .service("tenant-pricing")
        .find({
          query: {
            tenantId: 0,
          },
        })
        .then((res) => {
          this.defaultPricing = res;
        });
    },

    getCustomProductOptions() {
      feathersClient
        .service("tenant-products")
        .find({
          query: {
            isCustom: 1,
          },
        })
        .then((res) => {
          this.customProductOptions = res;
        });
    },

    queryPartnerProducts() {
      feathersClient
        .service("tenant-products")
        .find({
          query: {
            isPartner: 1,
          },
        })
        .then((res) => {
          this.partnerProducts = [...res];
        });
    },

    removeListeners() {
      feathersClient.service("tenants").removeListener("patched");
      feathersClient.service("tenants").removeListener("created");
      feathersClient.service("tenants").removeListener("removed");
    },

    refreshPartnerList() {
      this.findPartnerAccounts();
    },

    queryConduitAccountOptions() {
      feathersClient
        .service("tenants")
        .find()
        .then((res) => {
          this.conduitAccountOptions = res
            .filter((item) => !!item.conduitPrefix)
            .map(({ id, name }) => ({
              key: id,
              label: name,
              disabled: false,
            }));
        });
    },
  },
  created() {
    if (this.user) {
      if (+this.user.userRole === 1) {
        this.findPartnerAccounts();
        this.getDefaultPricing();
      }
      if (+this.user.userRole === 11) {
        this.getDefaultPricing();
      }
      this.queryAccounts();
      this.getIntegrationOptions();
      this.getCustomProductOptions();
      this.queryConduitAccountOptions();
    }

    //event listeners
    feathersClient.service("tenants").on("patched", (data) => {
      //find the record in the table data.
      const updateIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const updateAllIndex = this.allTenants.findIndex(
        (item) => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allTenants.splice(updateAllIndex, 1, data);
      }
    });

    feathersClient.service("tenants").on("created", (data) => {
      if (+this.user.userRole === 1) {
        this.tableData.push(data);
        this.allTenants.push(data);
      }

      if (+this.user.userRole === 3 || +this.user.userRole === 12) {
        const { tenantIds } = this.user;
        if (tenantIds.includes(+data.id)) {
          this.tableData.push(data);
          this.allTenants.push(data);
        }
      }

      if (+this.user.userRole === 11) {
        const { partnerId: userPartnerId } = this.user;
        if (userPartnerId === data.partnerId) {
          this.tableData.push(data);
          this.allTenants.push(data);
        }
      }
    });

    feathersClient.service("tenants").on("removed", (data) => {
      const indexToRemove = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const indexAllToRemove = this.allTenants.findIndex(
        (item) => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allTenants.splice(indexAllToRemove, 1);
      }
    });
  },

  beforeDestroy() {
    this.$refs.xGrid.$destroy();
    this.$refs.accountForm.$destroy();
    this.removeListeners();
  },

  mounted() {
    this.queryTimezones();
    this.queryRoutingProfiles();
    if (+this.user.userRole === 1) {
      this.queryPartnerProducts();
    }
  },
};
</script>
